import type { ComponentType, SVGProps } from 'react';
import { matchPath } from 'react-router-dom';
import {
  canvasPages,
  navGroups,
  screensOnPage,
  webPath,
  DOC_BOARDS,
  type Screen,
} from '@screens';
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  BuildingStorefrontIcon,
  TruckIcon,
  ChartBarIcon,
  RectangleStackIcon,
  ShieldCheckIcon,
  CalendarDaysIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  LinkIcon,
  TagIcon,
  QueueListIcon,
  ShoppingCartIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

export type NavigationIcon = ComponentType<SVGProps<SVGSVGElement>>;

export interface NavigationItem {
  name: string;
  href: string;
  icon: NavigationIcon;
  permission?: string;
  description: string;
}

export interface NavigationGroup {
  name: string;
  items: NavigationItem[];
}

/**
 * Canonical web navigation for routes that have a real implementation.
 * Permission names intentionally mirror the API's action-first vocabulary.
 * The API/database remain the authoritative authorization boundary; this list
 * only controls whether a navigation affordance is shown.
 */
export const navigationGroups: NavigationGroup[] = [
  {
    name: 'Work',
    items: [
      {
        name: 'Operations Center',
        href: '/work',
        icon: QueueListIcon,
        description: 'Start work, handle exceptions, and move between operational queues.',
      },
      {
        name: 'All Screens',
        href: '/screens',
        icon: Squares2X2Icon,
        description: 'Search every registered web screen and inspect mobile/source and release-gated work.',
      },
      {
        name: 'Dashboard',
        href: '/dashboard',
        icon: HomeIcon,
        description: 'Review warehouse KPIs and active operational exceptions.',
      },
    ],
  },
  {
    name: 'Plan & Release',
    items: [
      {
        name: 'Orders',
        href: '/orders',
        icon: ClipboardDocumentListIcon,
        permission: 'read:orders',
        description: 'Review, verify, and release customer and cruise-line demand.',
      },
      {
        name: 'MarineX',
        href: '/marinex',
        icon: TagIcon,
        permission: 'read:orders',
        description: 'Build checked work orders and label/export packages.',
      },
      {
        name: 'Packing Review',
        href: '/packing',
        icon: RectangleStackIcon,
        permission: 'read:orders',
        description: 'Review source-backed picks and packing exceptions before audited carton transactions.',
      },
      {
        name: 'Staging Carton Workbench',
        href: '/packing/cartons',
        icon: CubeIcon,
        permission: 'wrap:orders',
        description: 'Stage-only carton scans, seal and audit behind explicit database approval.',
      },
      {
        name: 'Waves',
        href: '/waves',
        icon: RectangleStackIcon,
        permission: 'read:orders',
        description: 'Sequence and execute directed picking work.',
      },
    ],
  },
  {
    name: 'Stock & Supply',
    items: [
      {
        name: 'Inventory',
        href: '/inventory',
        icon: CubeIcon,
        permission: 'read:inventory',
        description: 'Inspect stock, lots, availability, and inventory exceptions.',
      },
      {
        name: 'Inventory Control',
        href: '/inventory/control',
        icon: CubeIcon,
        permission: 'read:inventory',
        description: 'Review authoritative inventory totals and navigate stock exceptions.',
      },
      {
        name: 'Expiry Risk',
        href: '/inventory/expiry-risk',
        icon: CalendarDaysIcon,
        permission: 'read:inventory',
        description: 'Investigate expiring lots and overdue stock.',
      },
      {
        name: 'Stock Movements',
        href: '/inventory/movements',
        icon: QueueListIcon,
        permission: 'read:inventory',
        description: 'Inspect inventory movement and adjustment history.',
      },
      {
        name: 'Purchasing',
        href: '/purchasing',
        icon: ShoppingCartIcon,
        permission: 'read:inventory',
        description: 'Review purchase orders, vendors, receipt state, and procurement variances.',
      },
      {
        name: 'Supplier Inbound',
        href: '/purchasing/supplier-inbound',
        icon: ShoppingCartIcon,
        permission: 'read:inventory',
        description: 'Verified supplier purchase orders, authenticated scans, independent QC and controlled inventory posting.',
      },
      {
        name: 'Customer PO Review',
        href: '/purchasing/receiving',
        icon: ClipboardDocumentListIcon,
        permission: 'read:inventory',
        description: 'Reconcile cruise-line customer PO quantities and matching discrepancies; not physical stock receiving.',
      },
      {
        name: 'Locations',
        href: '/warehouse/locations',
        icon: BuildingStorefrontIcon,
        permission: 'read:warehouses',
        description: 'Manage warehouse locations and physical storage topology.',
      },
      {
        name: 'Quality',
        href: '/qc',
        icon: ShieldCheckIcon,
        permission: 'read:qc',
        description: 'Inspect, hold, disposition, and release quality-controlled stock.',
      },
    ],
  },
  {
    name: 'Ship & Yard',
    items: [
      {
        name: 'Dock',
        href: '/dock',
        icon: CalendarDaysIcon,
        permission: 'read:dock',
        description: 'Coordinate doors, appointments, and loading or receiving windows.',
      },
      {
        name: 'Delivery',
        href: '/delivery',
        icon: TruckIcon,
        permission: 'read:deliveries',
        description: 'Track outbound execution and proof-of-delivery status.',
      },
      {
        name: 'Fleet',
        href: '/fleet',
        icon: TruckIcon,
        permission: 'read:fleet',
        description: 'Manage vehicle readiness, inspections, and maintenance exceptions.',
      },
    ],
  },
  {
    name: 'Intelligence',
    items: [
      {
        name: 'Reports',
        href: '/reports',
        icon: ChartBarIcon,
        permission: 'read:reports',
        description: 'Analyze operational performance using defined warehouse metrics.',
      },
    ],
  },
  {
    name: 'Administration',
    items: [
      {
        name: 'Users',
        href: '/settings/users',
        icon: UsersIcon,
        permission: 'read:users',
        description: 'Manage users and operational access.',
      },
      {
        name: 'Warehouse Settings',
        href: '/settings/warehouse',
        icon: WrenchScrewdriverIcon,
        permission: 'admin:warehouses',
        description: 'Configure warehouse-level operating settings.',
      },
      {
        name: 'Integrations',
        href: '/settings/integrations',
        icon: LinkIcon,
        permission: 'admin:system',
        description: 'Review external-system configuration and integration health.',
      },
    ],
  },
];

export function canAccessNavigationItem(
  item: NavigationItem,
  hasPermission: (permission: string) => boolean
): boolean {
  return !item.permission || hasPermission(item.permission);
}

/* ------------------------------------------------------------------------------------------ */
/* Canvas navigation: the seven groups of the "Warehouse Platform — Core Screens" canvas.      */
/* Built once at module load from the screen registry; the sidebar, header search, home and    */
/* catalog all read from here so they agree on grouping, names and link targets.             */
/* ------------------------------------------------------------------------------------------ */

export interface CanvasNavPage {
  id: string;
  /** Short page label, e.g. "Receiving and quality intake" (MOD prefix / "replaces" note removed). */
  label: string;
  /** Full canvas page name. */
  fullName: string;
  screens: Screen[];
}

export interface CanvasNavGroup {
  id: string;
  label: string;
  pages: CanvasNavPage[];
  screenCount: number;
}

/** Shorten a canvas page name for navigation: "MOD-05 Receiving and quality intake" -> "Receiving and quality intake". */
export function shortPageName(name: string): string {
  return name
    .replace(/^MOD-\d+\s+/, '')
    .replace(/\s*\((?:replaces|replace)[^)]*\)\s*$/i, '')
    .replace(/\s+—\s+.*$/, '')
    .trim();
}

const pageNameById = new Map(canvasPages.map((p) => [p.id, p.name]));

/** Web navigation screens on a page: no phone screens, no platform documentation boards. */
export function navScreensOnPage(pageId: string): Screen[] {
  return screensOnPage(pageId).filter((s) => s.device !== 'phone' && !DOC_BOARDS.has(s.board));
}

export const canvasNavGroups: CanvasNavGroup[] = navGroups.map((g) => {
  const pages = g.pages
    .filter((id) => pageNameById.has(id))
    .map((id) => {
      const fullName = pageNameById.get(id) ?? id;
      return { id, label: shortPageName(fullName), fullName, screens: navScreensOnPage(id) };
    })
    .filter((p) => p.screens.length > 0);
  return {
    id: g.id,
    label: g.label,
    pages,
    screenCount: pages.reduce((n, p) => n + p.screens.length, 0),
  };
});

/** Canvas page id -> nav group id (for catalog filters and breadcrumbs). */
export const groupIdByPage = new Map<string, string>(
  navGroups.flatMap((g) => g.pages.map((p) => [p, g.id] as [string, string])),
);

/** Where a navigation link to a screen goes: the working page when one exists, else the shell. */
export function screenHref(s: Screen): string {
  return webPath(s);
}

/** True when an implemented page backs this web screen. */
export function isLiveOnWeb(s: Screen): boolean {
  return Boolean(s.wiredWeb);
}

/** Is this screen the one a URL shows? Handles wired routes with params (/orders/:id). */
export function screenMatchesPath(s: Screen, pathname: string): boolean {
  if (webPath(s) === pathname) return true;
  if (s.wiredWeb && s.wiredWeb.includes(':')) return matchPath(s.wiredWeb, pathname) !== null;
  return false;
}

export interface LocatedScreen {
  groupId: string;
  groupLabel: string;
  pageId: string;
  pageLabel: string;
  board: string;
  screen: Screen;
}

const locateCache = new Map<string, LocatedScreen | null>();

/** Find the navigation group, page and screen for a URL (first match in nav order). */
export function locateScreen(pathname: string): LocatedScreen | null {
  const hit = locateCache.get(pathname);
  if (hit !== undefined) return hit;
  let found: LocatedScreen | null = null;
  outer: for (const g of canvasNavGroups) {
    for (const p of g.pages) {
      for (const s of p.screens) {
        if (screenMatchesPath(s, pathname)) {
          found = { groupId: g.id, groupLabel: g.label, pageId: p.id, pageLabel: p.label, board: s.board, screen: s };
          break outer;
        }
      }
    }
  }
  if (locateCache.size > 500) locateCache.clear();
  locateCache.set(pathname, found);
  return found;
}
